import { useState, useEffect } from "react";
import { X, TrendingUp, TrendingDown } from "lucide-react";
import PriceChart from "@/components/market/PriceChart";
import api from "@/lib/api";
import { money } from "@/lib/format";

const RANGES = [{ k: 1, l: "24H" }, { k: 7, l: "7D" }, { k: 30, l: "30D" }];
const fmtPrice = (v) => "$" + Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: v < 10 ? 4 : 2 });

export default function CoinDetailModal({ coin, onClose }) {
  const [days, setDays] = useState(7);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coin) return;
    setLoading(true);
    api.get(`/market/crypto/${coin.id}/chart?days=${days}`)
      .then(({ data }) => setChart(data))
      .catch(() => setChart(null))
      .finally(() => setLoading(false));
  }, [coin, days]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  if (!coin) return null;
  const prices = chart ? chart.prices.map((x) => x.p) : [];
  const up = chart ? chart.last >= chart.first : (coin.change_24h || 0) >= 0;
  const changePct = chart && chart.first ? ((chart.last - chart.first) / chart.first) * 100 : coin.change_24h;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="coin-detail-modal">
      <div className="w-full max-w-2xl glass rounded-2xl p-6 relative animate-in" onClick={(e) => e.stopPropagation()}>
        <button data-testid="coin-modal-close" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-amber-400 p-1"><X className="w-5 h-5" /></button>

        <div className="flex items-center gap-3">
          <img src={coin.image} alt={coin.symbol} className="w-11 h-11 rounded-full" />
          <div>
            <div className="font-display font-bold text-xl">{coin.name} <span className="text-slate-500 text-sm">{coin.symbol}</span></div>
            <div className="flex items-center gap-3">
              <span className="font-mono-fin text-2xl font-extrabold gold-text">{fmtPrice(coin.price)}</span>
              <span className={`font-mono-fin text-sm font-bold flex items-center gap-1 ${up ? "text-emerald-400" : "text-red-400"}`}>
                {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{changePct >= 0 ? "+" : ""}{Number(changePct || 0).toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`range-${r.k}`} onClick={() => setDays(r.k)}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold ${days === r.k ? "gold-btn" : "border border-amber-500/20 text-slate-400 hover:text-amber-300"}`}>{r.l}</button>
          ))}
        </div>

        <div className="mt-4 rounded-xl bg-black/30 border border-amber-500/15 p-3">
          {loading ? <div className="h-[240px] grid place-items-center text-amber-400 font-mono-fin text-sm">Loading chart...</div>
            : <PriceChart data={prices} up={up} height={240} />}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            { l: "Market Cap", v: money(coin.market_cap, 0) },
            { l: "24h Volume", v: money(coin.volume, 0) },
            { l: `${days}D High`, v: chart ? fmtPrice(chart.high) : "-" },
            { l: `${days}D Low`, v: chart ? fmtPrice(chart.low) : "-" },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-black/40 border border-amber-500/15 p-3">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">{s.l}</div>
              <div className="font-mono-fin text-sm font-bold mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
